import { useEffect, useState } from "react";
import { useParams, Link } from "react-router-dom";
import OakLogo from "@/components/OakLogo";
import LoadingScreen from "@/components/LoadingScreen";
import { api } from "@/lib/api";
import { CheckCircle2, ShieldAlert, ExternalLink, BookOpen, Share2, Copy } from "lucide-react";
import { toast } from "sonner";

/**
 * Public, auth-free study guide page. Mirrors CertificatePublic in feel
 * — same OAK Dark + gradient hero — so shared OG cards and the landing
 * page feel like one product. The list of clipped snippets is the
 * primary content; an "open Oak AI Campus" CTA at the bottom converts.
 */
export default function PublicNote() {
  const { slug } = useParams();
  const [note, setNote] = useState(null);
  const [error, setError] = useState(null);

  useEffect(() => {
    api
      .get(`/notes/${slug}`)
      .then(({ data }) => setNote(data))
      .catch((e) => setError(e?.response?.data?.detail || "Note not found"));
  }, [slug]);

  if (error) {
    return (
      <div className="min-h-screen grid place-items-center px-6" data-testid="public-note-not-found">
        <div className="glass p-8 max-w-md w-full text-center space-y-3">
          <ShieldAlert size={28} className="text-rose-300 mx-auto" />
          <h1 className="font-display text-2xl tracking-tight text-white">Study guide not found</h1>
          <p className="text-white/55 text-sm">
            The slug <span className="font-mono-oak text-white/75">{slug}</span> doesn't match any
            published OAK study guide.
          </p>
          <Link to="/" className="inline-flex items-center gap-1.5 text-sm text-[#8aa8ff] hover:text-white transition">
            Visit Oak AI Campus <ExternalLink size={12} />
          </Link>
        </div>
      </div>
    );
  }

  if (!note) return <LoadingScreen label="Loading study guide…" />;

  const apiOrigin = (api.defaults.baseURL || "").replace(/\/api\/?$/, "");
  const ogShareUrl = `${apiOrigin}/api/og/notes/${note.slug}`;

  const shareLink = async () => {
    try {
      if (navigator.share) {
        await navigator.share({
          title: note.title,
          text: `${note.owner_name}'s study guide on "${note.title}" — via Oak AI Campus.`,
          url: ogShareUrl,
        });
        return;
      }
      await navigator.clipboard.writeText(ogShareUrl);
      toast.success("Share link copied");
    } catch (e) {
      if (e?.name !== "AbortError") toast.error("Could not share");
    }
  };

  return (
    <div className="min-h-screen px-6 md:px-10 py-10" data-testid="public-note-page">
      <header className="max-w-4xl mx-auto flex items-center justify-between flex-wrap gap-3">
        <Link to="/" className="flex items-center gap-2">
          <OakLogo size={28} glow />
          <div className="leading-tight">
            <div className="font-display font-semibold text-sm">Oak AI Campus</div>
            <div className="label-eyebrow !text-[9px]">Public Study Guide</div>
          </div>
        </Link>
        <div className="flex items-center gap-2">
          <span
            className="inline-flex items-center gap-1.5 rounded-full border border-[#8aa8ff]/40 bg-[#0044FF]/[0.08] px-3 py-1 text-[11px] text-[#8aa8ff]"
            data-testid="public-note-views"
          >
            <CheckCircle2 size={12} /> {note.views} view{note.views === 1 ? "" : "s"}
          </span>
          <button
            onClick={shareLink}
            className="inline-flex items-center gap-1.5 rounded-full border border-[#00FF9D]/40 bg-[#00FF9D]/[0.08] px-3 py-1.5 text-[11px] text-[#00FF9D] hover:bg-[#00FF9D]/[0.16] transition"
            data-testid="public-note-share"
          >
            <Share2 size={12} /> Share
          </button>
        </div>
      </header>

      <main className="max-w-4xl mx-auto mt-10">
        <div
          className="relative rounded-3xl overflow-hidden border border-white/15 p-8 md:p-12"
          style={{
            background:
              "radial-gradient(900px circle at 15% 0%, rgba(255,123,0,0.22), transparent 55%), radial-gradient(700px circle at 100% 100%, rgba(93,63,211,0.30), transparent 55%), linear-gradient(180deg, #0B0E14 0%, #131720 100%)",
          }}
          data-testid="public-note-hero"
        >
          <div className="flex items-center gap-2 mb-6">
            <BookOpen size={14} className="text-[#00FF9D]" />
            <span className="label-eyebrow !text-[10px]">Study Guide · {note.snippets?.length || 0} clip{(note.snippets?.length || 0) === 1 ? "" : "s"}</span>
          </div>
          <h1
            className="font-display text-3xl md:text-5xl tracking-tight text-white leading-tight"
            data-testid="public-note-title"
          >
            {note.title}
          </h1>
          <div className="mt-4 text-white/65 text-sm">
            Curated by <span className="text-white font-semibold" data-testid="public-note-owner">{note.owner_name}</span>
            {note.created_at && (
              <span className="text-white/45">
                {" "}
                · {new Date(note.created_at).toLocaleDateString(undefined, { month: "short", day: "numeric", year: "numeric" })}
              </span>
            )}
          </div>
        </div>

        <ol className="mt-8 space-y-3" data-testid="public-note-snippets">
          {(note.snippets || []).map((s, i) => (
            <li
              key={i}
              className="rounded-xl border border-white/10 bg-white/[0.03] p-5 hover:bg-white/[0.05] transition"
              data-testid={`public-note-snippet-${i}`}
            >
              <div className="text-[10px] uppercase tracking-wider text-white/40 mb-2 font-mono-oak">
                #{i + 1}
                {s.source_title ? <span className="text-white/55"> · {s.source_title}</span> : null}
              </div>
              <p className="text-[15px] text-white/85 leading-relaxed whitespace-pre-wrap break-words">{s.text}</p>
            </li>
          ))}
        </ol>

        <div className="mt-12 rounded-2xl border border-[#0044FF]/40 bg-[#0044FF]/[0.07] p-6 text-center" data-testid="public-note-cta">
          <div className="font-display text-xl text-white">Build your own study guide</div>
          <p className="text-white/65 text-sm mt-1">Tell Oak AI Campus your goal and ship a 5-step path in seconds. Free to start.</p>
          <Link
            to="/"
            className="btn-primary inline-flex items-center gap-1.5 rounded-full px-5 py-2.5 mt-4 text-sm font-display font-semibold"
            data-testid="public-note-cta-btn"
          >
            Master anything <ExternalLink size={13} />
          </Link>
        </div>
      </main>
    </div>
  );
}
